import { usePermissions } from "./usePermissions";
import {
  CASES_PERMS,
  MANAGE_WORKSPACE_PERMS,
  P,
} from "@/constants/permissions";
import { useSpotUser } from "@/utils/spotUser";
import { isCaseManagementEnabled } from "@/utils/modules";
import { sectionHref } from "@/components/Layouts/pageSections";

interface DashboardRoute {
  href: string;
  requiredPermissions: string[];
  level: "tenant" | "group";
}

/**
 * Ordered list of dashboard routes matching the sidenav.
 * The first route the user has permission for becomes their landing page.
 *
 * Order matters: Workshop is the canonical landing page for anyone with a
 * view grant (read/edit/delete) — must match the page's own gate or Readers
 * (run-only) get redirected onto a blank Workshop and stay stuck. App
 * library follows for run-only users. Past usage covers the history-only
 * viewer. A case role then stays on the main page, where the inboxes are.
 */
export const DASHBOARD_ROUTES: DashboardRoute[] = [
  {
    href: "/",
    requiredPermissions: [P.RESOURCE_READ, P.RESOURCE_EDIT, P.RESOURCE_DELETE],
    level: "group",
  },
  {
    href: "/apps",
    requiredPermissions: [P.RESOURCE_RUN, P.RESOURCE_EDIT],
    level: "group",
  },
  {
    href: "/past-usage",
    requiredPermissions: [
      P.RESOURCE_HISTORY,
      P.RESOURCE_RUN,
      P.RESOURCE_EDIT,
      P.EXECUTIONS_RETRY,
    ],
    level: "group",
  },
];

// After the case roles, the rest of the sidenav in its order: Analytics and
// Connections (main sidenav sections of Manage workspace), then Manage
// workspace itself.
const WORKSPACE_ROUTES: DashboardRoute[] = [
  {
    href: sectionHref("/manage-workspace", "analytics"),
    requiredPermissions: [P.WORKSPACE_ANALYTICS],
    level: "group",
  },
  {
    href: sectionHref("/manage-workspace", "linked-accounts"),
    requiredPermissions: [P.INTEGRATIONS_EDIT],
    level: "group",
  },
  {
    href: "/manage-workspace",
    requiredPermissions: MANAGE_WORKSPACE_PERMS,
    level: "group",
  },
];

export function useFirstAllowedRoute(): {
  route: string;
  isLoading: boolean;
} {
  const { hasAnyPermission, isLoading, isFresh, permissions } =
    usePermissions();
  const { spotGroup } = useSpotUser();
  const canSeeCases =
    isCaseManagementEnabled(spotGroup) &&
    hasAnyPermission(CASES_PERMS, "group");

  // Wait until group permissions are actually resolved before deciding
  // the landing page. Without this, the brief gap after login where
  // group_permissions is null causes an immediate redirect to the fallback.
  // A stale copy (cached from an older role, or the previous workspace's)
  // would send the user to a page this role cannot use.
  if (isLoading || !isFresh || !permissions?.group_permissions) {
    return { route: "/", isLoading: true };
  }

  for (const entry of DASHBOARD_ROUTES) {
    if (hasAnyPermission(entry.requiredPermissions, entry.level)) {
      return { route: entry.href, isLoading: false };
    }
  }

  if (canSeeCases) {
    return { route: "/", isLoading: false };
  }

  for (const entry of WORKSPACE_ROUTES) {
    if (hasAnyPermission(entry.requiredPermissions, entry.level)) {
      return { route: entry.href, isLoading: false };
    }
  }

  return { route: "/", isLoading: false };
}
